import { CustomQueryOptions } from '@/app/common/types_and_interfaces'
import { queryOptions } from '@tanstack/react-query'
import {
  BankAccount,
  PaginatedProgramBankAccountsList,
  Processor,
} from './schema'
import {
  getAllProgramBankAccounts,
  getBankAccountDetail,
  getPaginatedProgramBankAccountLists,
  getProcessors,
} from './requests'

const BankAccountQueryKeys = {
  list_all: ['bank_account_list'],
  all_by_program: (programId: number) => [
    'bank_account_list',
    'all',
    programId,
  ],
  detail: ['bank_account_detail'],
  processors: (programId: number) => ['processors', programId],
  paginate_by_program: (
    programId: number,
    recordsPerPage: number,
    currentPage: number,
    search?: string | undefined
  ) => [
    ...BankAccountQueryKeys.list_all,
    programId,
    recordsPerPage,
    currentPage,
    search,
  ],
  details: (bankAccountId: number) => [
    ...BankAccountQueryKeys.detail,
    bankAccountId,
  ],
}

export class BankAccountQueries {
  public static getPaginatedListByProgramId(
    recordsPerPage: number,
    currentPage: number,
    programId: number = 0,
    search?: string,
    options: CustomQueryOptions<PaginatedProgramBankAccountsList> = {}
  ) {
    return queryOptions({
      queryKey: BankAccountQueryKeys.paginate_by_program(
        programId,
        recordsPerPage,
        currentPage,
        search
      ),
      queryFn: () =>
        getPaginatedProgramBankAccountLists(
          programId,
          currentPage,
          recordsPerPage,
          search
        ),
      ...options,
    })
  }

  public static getAllByProgramId(
    programId: number,
    options: CustomQueryOptions<BankAccount[]> = {}
  ) {
    return queryOptions({
      queryKey: BankAccountQueryKeys.all_by_program(programId),
      queryFn: () => getAllProgramBankAccounts(programId),
      ...options,
    })
  }

  public static getById(
    id: string,
    options: CustomQueryOptions<BankAccount> = {}
  ) {
    const parsedId = Number.parseInt(id, 10)
    return queryOptions({
      queryKey: BankAccountQueryKeys.details(parsedId),
      queryFn: () => getBankAccountDetail(id),
      enabled: Number.isFinite(parsedId),
      ...options,
    })
  }

  public static getProcessors(
    programId: number,
    options: CustomQueryOptions<Processor[]> = {}
  ) {
    return queryOptions({
      queryKey: BankAccountQueryKeys.processors(programId),
      queryFn: () => getProcessors(programId),
      ...options,
    })
  }
}
